import { apiFetch } from "@/lib/apiClient";

/**
 * Client for app/modules/medication_normalization/router.py — mounted at
 * /api/patient/medication-matching/{uploadId}. The ONE place in this app
 * that reads the RxNorm engine's per-medication fields (rxcui, match level,
 * confidence band, etc.) — every other medication endpoint deliberately
 * scrubs them. Powers the "Medication Matching" page only.
 */
function actingAsHeaders(actingAsProfileId?: string): Record<string, string> {
  return actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
}

const BASE_PATH = "/api/patient/medication-matching";

// L0-L4 — see medication_normalization/cascade.py. Null while the row hasn't
// resolved yet (normalizationStatus "pending"/"failed"/"skipped").
export type MedicationMatchLevel = "L0" | "L1" | "L2" | "L3" | "L4" | null;
export type MedicationConfidenceBand = "accept" | "review" | "reject" | null;
export type MedicationNormalizationStatus = "pending" | "resolved" | "failed" | "skipped";

export interface MedicationMatch {
  id: string;
  medicationName: string;
  cleanedMedicationText: string | null;
  rxcui: string | null;
  matchLevel: MedicationMatchLevel;
  confidenceBand: MedicationConfidenceBand;
  confidenceScore: number | null;
  reviewReason: string | null;
  normalizationStatus: MedicationNormalizationStatus;
}

interface MedicationMatchListResponse {
  data: MedicationMatch[];
}

/** One upload's medications with their RxNorm resolution — ownership is
 *  enforced server-side (see router.py); a foreign/nonexistent uploadId
 *  just comes back empty, never a leaked 403/404 distinction. */
export async function getUploadMedicationMatches(
  uploadId: string,
  actingAsProfileId?: string,
): Promise<MedicationMatch[]> {
  const { data, error } = await apiFetch<MedicationMatchListResponse>(`${BASE_PATH}/${uploadId}`, {
    method: "GET",
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
  });
  if (error) {
    throw new Error(error.message || "Failed to load this upload's medications.");
  }
  // A 2xx response whose body failed to parse (`data` itself null) is a
  // real failure, not "this upload genuinely has zero medications" — only
  // the latter (a present envelope with a real empty `data` array) is a
  // legitimate empty result.
  if (!data) {
    throw new Error("Received an empty or invalid response. Please try again.");
  }
  return data.data;
}
